// src/component/Layout/Footer/Footer.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import footerlogo from "../../../assets/images/logo.png";

const FooterSection = styled.footer`
  background: #151414;
  color: #e6e6e6;
  position: relative;
  isolation: isolate;
  /* subtle top border glow */
  &::before{
    content:"";
    position:absolute; inset:0 auto auto 0;
    height: 3px; width: 100%;
    background: linear-gradient(90deg,#07b6f2, #0ac2ff 40%, #79e8ff 70%, transparent);
    opacity:.8;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 15px 10px 15px;
`;

const CtaSection = styled.div`
  border-bottom: 1px solid #373636;
  padding-bottom: 30px;
`;

const Cta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  justify-content: space-between;
`;

const SingleCta = styled.address`
  flex: 1 1 100%;
  margin-bottom: 20px;
  font-style: normal;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (min-width: 768px) {
    flex: 0 0 32%;
  }
`;

const CtaIcon = styled.i`
  color: #ff5e14;
  font-size: 30px;
  margin-top: 4px;
`;

const CtaText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Heading = styled.h4`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 6px;
`;

const ExtLink = styled.a`
  color: rgb(230,230,230);
  text-decoration: none;
  transition: color .25s ease, text-decoration-color .25s ease;
  text-underline-offset: 3px;

  &:hover, &:focus-visible {
    color: #ff5e14;
    text-decoration: underline;
  }
`;

const ContentSection = styled.div`
  padding-top: 50px;
`;

const FooterLogo = styled.div`
  margin-bottom: 24px;
  a {
    display: inline-flex;
    align-items: center;
    max-width: 300px;
    padding: 12px 16px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
  }
  img {
    display: block;
    max-width: 260px;
    width: 100%;
    height: auto;
  }

  @media (max-width: 575px) {
    a {
      max-width: 245px;
      padding: 10px 12px;
    }
  }
`;

const FooterText = styled.p`
  margin-bottom: 8px;
  font-size: 16.5px;
  color: #ffffff;
  line-height: 1.8;
`;

const Widget = styled.nav`
  margin-bottom: 0px;

  h3 {
    color: #fff;
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 30px;
    position: relative;
  }
  h3::before {
    content: "";
    position: absolute;
    left: 0;
    bottom: -10px;
    height: 2px;
    width: 50px;
    background: #ff5e14;
  }

  ul { list-style: none; padding: 0; margin: 0; }
  li {
    display: inline-block;
    width: 50%;
    margin-bottom: 12px;
  }
  a {
    color: #878787;
    text-transform: capitalize;
    text-decoration: none;
    transition: color .25s ease, text-decoration-color .25s ease;
    text-underline-offset: 3px;
  }
  a:hover, a:focus-visible { color: #ff5e14; text-decoration: underline; }
`;

const CopyRight = styled.div`
  background: #202020;
  padding: 25px 0;
  p {
    margin: 0;
    font-size: 14px;
    color: #878787;
  }
  a { color: #ff5e14; }
`;

const SocialMediaSection = styled.div`
  margin-top: 6px;
  p {
    margin-bottom: 10px;
    color: #fff;
    font-size: 20px;
  }
  .social-icons {
    display: flex;
    gap: 14px;
  }
  .social-icons a {
    width: 38px; height: 38px;
    display: grid; place-items: center;
    border-radius: 10px;
    background: #1c1c1c;
    color: #e6e6e6;
    border: 1px solid #2b2b2b;
    transition: transform .2s ease, border-color .2s ease, background .2s ease;
  }
  .social-icons a:hover,
  .social-icons a:focus-visible {
    transform: translateY(-2px);
    border-color: #07b6f2;
    background: #0f2330;
  }
`;

const BackToTop = styled.button`
  position: fixed;
  right: 18px; bottom: 18px;
  width: 44px; height: 44px;
  border-radius: 999px;
  border: 0;
  background: #10b5ff;
  color: #fff;
  box-shadow: 0 10px 24px rgba(16,24,40,.2);
  cursor: pointer;
  display: grid; place-items: center;
  transition: transform .2s ease, opacity .2s ease;
  opacity: ${p => (p.$show ? 1 : 0)};
  pointer-events: ${p => (p.$show ? "auto" : "none")};
  z-index: 50;
  &:hover { transform: translateY(-2px); }
  &:focus-visible { outline: 3px solid #79e8ff; outline-offset: 2px; }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <FooterSection id="site-footer" role="contentinfo" aria-label="Site footer">
      <Container>
        {/* ---- CTA Row ---- */}
        <CtaSection aria-label="Contact information">
          <Cta>
            <SingleCta>
              <CtaIcon className="fas fa-map-marker-alt" aria-hidden="true" />
              <CtaText>
                <Heading>Find us</Heading>
                <ExtLink
                  href="https://maps.app.goo.gl/nPjk9XgHVn8Jqfyq7"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open location in Google Maps"
                >
                  NexTech PLM Solutions Pvt. Ltd., 2nd Floor, The Capitol, New DP Rd,
                  Vishal Nagar, Pimple Nilakh, Pune, Pimpri-Chinchwad, Maharashtra 411027
                </ExtLink>
              </CtaText>
            </SingleCta>

            <SingleCta>
              <CtaIcon className="far fa-envelope-open" aria-hidden="true" />
              <CtaText>
                <Heading>Mail us</Heading>
                <ExtLink href="mailto:info@nextechplm.com" aria-label="Email info@nextechplm.com">
                  info@nextechplm.com
                </ExtLink>
              </CtaText>
            </SingleCta>
          </Cta>
        </CtaSection>

        {/* ---- Main Footer Content ---- */}
        <ContentSection>
          <div className="row" aria-label="Footer navigation">
            <div className="col-lg-4 mb-4">
              <Widget aria-label="About NexTech">
                <FooterLogo>
                  <NavLink to="/" aria-label="NexTech PLM — Home">
                    <img
                      src={footerlogo}
                      alt="NexTech PLM Solutions logo"
                      loading="lazy"
                      decoding="async"
                      width="260"
                      height="80"
                    />
                  </NavLink>
                </FooterLogo>
                <FooterText>
                  NexTech is a top PLM and Engineering solutions provider, empowering global
                  customers in digital transformation with Teamcenter PLM as their core platform.
                </FooterText>
              </Widget>
            </div>

            <div className="col-lg-4 mb-4">
              <Widget aria-labelledby="footer-links">
                <h3 id="footer-links">Useful Links</h3>
                <ul>
                  <li><NavLink to="/">Home</NavLink></li>
                  <li><NavLink to="/about">About us</NavLink></li>
                  <li><NavLink to="/career">Career</NavLink></li>
                  <li><NavLink to="/contact">Contact Us</NavLink></li>
                  <li><NavLink to="/blog">Blogs</NavLink></li>
                  <li><NavLink to="/teamcenter-mcad-integration">MCAD integration</NavLink></li>
                  <li><NavLink to="/teamcenter-ecad-integration">ECAD integration</NavLink></li>
                  <li><NavLink to="/teamcenter-cybersecurity">Teamcenter cybersecurity</NavLink></li>
                  <li><NavLink to="/teamcenter-implementation">Teamcenter Implementation</NavLink></li>
                </ul>
              </Widget>
            </div>

            <div className="col-lg-4 mb-4">
              <Widget aria-labelledby="footer-solutions">
                <h3 id="footer-solutions">Our Solution</h3>
                <ul>
                  <li><NavLink to="/mendix-apps-solutions">Mendix Apps Solution</NavLink></li>
                  <li><NavLink to="/polarion-alm-solution">Polarian ALM Solution</NavLink></li>
                  <li><NavLink to="/sharepoint-insight-pdm-implementation">SharePoint</NavLink></li>
                  <li><NavLink to="/teamcenter-staff-augmentation-services">Staff Augmentation</NavLink></li>
                  <li><NavLink to="/teamcenter-software-development-services">Software Development</NavLink></li>
                  <li><NavLink to="/teamcenter-support-consulting-services">Support and Consulting</NavLink></li>
                  <li><NavLink to="/teamcenter-plm-solutions-amc">PLM Solutions AMC</NavLink></li>
                  <li><NavLink to="/teamcenter-redefining-plm-solution">Redefining PLM Solutions</NavLink></li>
                </ul>
              </Widget>
            </div>
          </div>

          {/* Social Media */}
          <SocialMediaSection aria-label="Social media">
            <p>Connect with us:</p>
            <div className="social-icons">
              <a
                href="https://www.linkedin.com/company/nextech-plm-solutions/"
                className="linkedin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                title="LinkedIn"
              >
                <i className="fa-brands fa-linkedin" aria-hidden="true" />
              </a>
              <a
                href="https://www.facebook.com/share/vNf8TQB9UWD12RVY/?mibextid=qi2Omg"
                className="facebook"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                title="Facebook"
              >
                <i className="fa-brands fa-facebook-f" aria-hidden="true" />
              </a>
              <a
                href="https://twitter.com/NextechPlm"
                className="twitter"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                title="X (Twitter)"
              >
                <i className="fa-brands fa-x-twitter" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/nextech_plm_solutions?igsh=MTUwOWtvM3h2cHJrdw=="
                className="instagram"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                title="Instagram"
              >
                <i className="fa-brands fa-instagram" aria-hidden="true" />
              </a>
            </div>
          </SocialMediaSection>
        </ContentSection>
      </Container>

      <CopyRight>
        <div>
          <p className="text-center">
            Copyright &copy; {currentYear}, All Rights Reserved
          </p>
        </div>
      </CopyRight>

      {/* Back to top */}
      <BackToTop
        type="button"
        onClick={scrollToTop}
        $show={showTop}
        aria-label="Back to top"
        title="Back to top"
      >
        <i className="fa-solid fa-arrow-up" aria-hidden="true" />
      </BackToTop>
    </FooterSection>
  );
};

export default Footer;
